import { useState } from 'react';
import { Link } from 'react-router-dom';
import BeforeAfterComparison from './BeforeAfterComparison';

const CLIPS = [
  {
    title: 'Studio Wall to Sunlit Living Room',
    project: 'Beverage Commercial · Set Extension & Warm Relight',
    beforeLabel: 'Bare Studio Wall Plate',
    afterLabel: 'AI Living Room & Daylight VFX',
    description:
      'A bare indoor wall behind the actress holding the juice bottle is replaced with a sunlit living room interior, framed wall art, and warm window bounce light.',
    before:
      'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/scene10.mp4?alt=media&token=9cd3170e-292e-4397-bd7a-5c71a654dd67',
    after:
      'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/1080P.mp4?alt=media&token=e3bfa8df-ff1f-43fc-87d2-8a264ad30fc2',
  },
  {
    title: 'Blue-Screen to Smart Bottling Plant',
    project: 'Industrial Commercial · Chroma Key to Factory Floor',
    beforeLabel: 'Blue-Screen Chroma Plate',
    afterLabel: 'AI Automated Factory Floor VFX',
    description:
      'Talent shot against a studio blue screen with two reference monitors is composited into an automated bottling factory with robotic arms and moving conveyor lines.',
    before:
      'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/scene9h.mp4?alt=media&token=d4744eaa-7e77-44bd-9fd4-b8154043c844',
    after:
      'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/1.mp4?alt=media&token=64ffea7a-93a5-4f2f-b779-ea76a56d7d3e',
  },
  {
    title: 'Green-Screen to 40,000-Seat Stadium',
    project: 'Telecom Stadium Campaign · Crowd & Arena Synthesis',
    beforeLabel: 'Studio Green-Screen Selfie',
    afterLabel: '40,000-Seat Floodlit Stadium VFX',
    description:
      'Fans taking a selfie on a studio green screen are integrated into a roaring 40,000-seat football stadium with foreground spectators, flags, and arena lighting.',
    before:
      'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/scene6bh.mp4?alt=media&token=bee7c601-acda-4e50-b1a8-40d2bbb022f7',
    after:
      'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/With%20Foreground.mp4?alt=media&token=b927ab55-0e98-495d-be57-0e6890a8d435',
  },
  {
    title: 'Overcast Sky to Golden-Hour Mountains',
    project: 'Telugu Action Feature Film · Beeble SwitchX Relighting',
    beforeLabel: 'Flat Overcast Location Plate',
    afterLabel: 'Golden-Hour Relight & Mountain Valley',
    description:
      'A flat grey daytime exterior shot of village fighters is relit with warm golden-hour rim lighting on the actors and extended with a banyan tree and mountain backdrop.',
    before:
      'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/SHOT_04_Source.mp4?alt=media&token=87bee864-7cf6-4b16-976d-d3f82b7886fd',
    after:
      'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/SHOT_04_SwitchX.mp4?alt=media&token=2b563974-24b6-425d-ba63-257f1112f7fa',
  },
  {
    title: 'AI Facial Age-Progression VFX',
    project: 'BKIC Insurance Ramadan Campaign · Character Aging',
    beforeLabel: 'Original Young Actress Plate',
    afterLabel: 'AI Age-Progressed Character',
    description:
      'Frame-consistent AI facial aging transforms the actress across decades while preserving her exact micro-expressions, glasses reflection, and natural doorway lighting.',
    before:
      'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/Before.mp4?alt=media&token=be31d9c6-cf1f-4224-b7d7-cf2c9bf2ab34',
    after:
      'https://firebasestorage.googleapis.com/v0/b/motionx-studio.firebasestorage.app/o/After.mp4?alt=media&token=a0e3e811-8792-4b4f-b8d4-db5d00a98867',
  },
];

export default function BeforeAfterSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeClip = CLIPS[activeIndex];

  return (
    <section
      id="before-after"
      className="bg-[#050505] py-16 sm:py-24 lg:py-28 border-t border-white/[0.08] w-full"
    >
      <div className="w-full px-5 sm:px-10 lg:px-16 xl:px-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-8 border-b border-white/[0.08]">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-[#E50914] mb-2">
              ● Hybrid Live-Action + AI VFX &nbsp;·&nbsp; Raw Camera Plate vs. Final Composite
            </p>
            <h2 className="text-white font-normal tracking-[-0.03em] leading-[1.05] text-[clamp(1.65rem,3.4vw,3.25rem)]">
              Hybrid VFX Transformations
            </h2>
            <p className="text-[#767d88] text-[clamp(0.85rem,1.05vw,1rem)] mt-2 max-w-2xl">
              Drag the interactive divider to compare raw studio &amp; location camera plates
              against MotionX’s final AI VFX composites.
            </p>
          </div>

          <Link
            to="/case-studies/stadium-vfx"
            className="text-[12px] sm:text-[13px] text-white/80 hover:text-white border border-white/15 px-4 py-2 rounded-full transition-colors no-underline self-start lg:self-end shrink-0"
          >
            Read Stadium VFX Case Study →
          </Link>
        </div>

        {/* Accurate Footage-Matched Shot Selector Pills */}
        <div className="flex flex-wrap items-center gap-2 pt-6 pb-5">
          {CLIPS.map((clip, i) => {
            const active = i === activeIndex;
            return (
              <button
                key={clip.title}
                type="button"
                onClick={() => setActiveIndex(i)}
                className={`px-3.5 sm:px-4 py-2 rounded-full text-[12px] sm:text-[13px] tracking-[-0.01em] transition-all cursor-pointer text-left ${
                  active
                    ? 'bg-[#E50914] text-white font-medium shadow-[0_0_20px_rgba(229,9,20,0.3)]'
                    : 'bg-white/[0.05] text-white/70 hover:bg-white/[0.1] hover:text-white'
                }`}
              >
                0{i + 1} &nbsp;·&nbsp; {clip.title}
              </button>
            );
          })}
        </div>

        {/* Active Shot Context Bar */}
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[13px]">
          <p className="text-white font-medium">
            {activeClip.title}{' '}
            <span className="text-[#767d88] font-normal">— {activeClip.project}</span>
          </p>
          <p className="text-[#8a909a] text-[12px] sm:text-[13px] max-w-2xl">
            {activeClip.description}
          </p>
        </div>

        {/* Comparison Viewport */}
        <div className="aspect-video rounded-xl overflow-hidden border border-white/[0.08] bg-black">
          <BeforeAfterComparison
            key={activeIndex}
            beforeSrc={activeClip.before}
            afterSrc={activeClip.after}
            beforeLabel={activeClip.beforeLabel}
            afterLabel={activeClip.afterLabel}
          />
        </div>
      </div>
    </section>
  );
}
